/**
 * Where a past ticket's tool answers come from in a Mocked run: the AI turn Kim recorded on it
 * before anyone replied, or, for a ticket with none, the scenario world.
 */

import type { Scenario } from '../types';

export type TicketSource = 'recorded' | 'imported';

export const TICKET_SOURCE_LABELS: Record<TicketSource, string> = {
	recorded: 'Recorded',
	imported: 'Imported'
};

type SourcedScenario = Pick<Scenario, 'kind' | 'recording'>;

/** A past ticket's source; null for a fabricated scenario, and when the server could not say. */
export const ticketSource = (scenario: SourcedScenario): TicketSource | null => {
	if (scenario.kind !== 'ticket' || scenario.recording === undefined) return null;
	return scenario.recording ? 'recorded' : 'imported';
};

/** A recorded turn's day as the screens write dates: "Oct 20, 2026". */
const recordedDay = (iso: string): string | null => {
	const date = new Date(iso);
	return Number.isNaN(date.getTime())
		? null
		: date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

/** What a ticket's source means for a Mocked run, in a sentence. */
export const ticketSourceHint = (scenario: SourcedScenario): string | null => {
	const source = ticketSource(scenario);
	if (source === 'imported') return 'No AI turn was recorded on this ticket. A Mocked run answers from the scenario world.';
	if (source !== 'recorded' || !scenario.recording) return null;
	const day = recordedDay(scenario.recording.recordedAt);
	return `Kim's AI handled this ticket${day ? ` on ${day}` : ''}. A Mocked run replays the tool answers it got then, when the recording can be read.`;
};

export const recordedCount = (scenarios: SourcedScenario[]): number =>
	scenarios.filter((scenario) => ticketSource(scenario) === 'recorded').length;

/** A counts line with its recorded tickets added: "12 past tickets · 3 fabricated · 5 recorded". */
export const withRecorded = (line: string, recorded: number): string =>
	recorded ? `${line} · ${recorded} ${TICKET_SOURCE_LABELS.recorded.toLowerCase()}` : line;
